import { useState } from 'react';
import { ChevronDown, ChevronRight, CornerDownLeft, Loader2, Wand2 } from 'lucide-react';
import type { document } from '@/lib/client';
import { useDocumentsStore } from '@/stores/documentsStore';

/**
 * A round's questions and assumptions, and what the person does next: answer (a question, or correct an assumption)
 * and let ZAC read the document again with the answers, or make the plan with them taken as decided. Each question
 * comes filled with ZAC's suggestion; one left empty stays open in the plan: its step asks for the value, or a Zoom
 * default stands.
 */
export function DocumentQuestions({ doc, compact = false }: { doc: document.DocumentView; compact?: boolean }) {
  // In the chat's card the assumptions start folded: Zoom's defaults rarely need a word, and the buttons stay in view.
  const [showAssumptions, setShowAssumptions] = useState(!compact);
  const suggested: Record<string, string> = Object.fromEntries(doc.questions.flatMap((q) => (q.suggestion === null ? [] : [[q.id, q.suggestion.text]])));
  const draft = useDocumentsStore((s) => s.drafts[`${doc.id}:${doc.round}`]);
  const setDraft = useDocumentsStore((s) => s.setDraft);
  const given = draft ?? suggested;
  const setGiven = (next: (prev: Record<string, string>) => Record<string, string>) => setDraft(doc.id, doc.round, next(given));
  const answer = useDocumentsStore((s) => s.answer);
  const plan = useDocumentsStore((s) => s.plan);
  const busy = useDocumentsStore((s) => s.busy.answer || s.busy.plan || false);
  const answers = Object.entries(given).filter(([, text]) => text.trim() !== '').map(([answers, text]) => ({ answers, text: text.trim() }));
  // ZAC's own suggestions are no reason for another (paid) read: they go with the plan as they are.
  const onlySuggested = answers.every((a) => suggested[a.answers] === a.text);
  const field = (id: string, placeholder: string) => (
    <input
      value={given[id] ?? ''}
      onChange={(e) => setGiven((prev) => ({ ...prev, [id]: e.target.value }))}
      disabled={busy}
      placeholder={placeholder}
      className="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
    />
  );

  return (
    <div className="space-y-4">
      {doc.questions.length > 0 && (
        <section className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <h2 className="text-sm font-medium text-amber-300">{doc.questions.length === 1 ? 'One question' : `${doc.questions.length} questions`} the document leaves open</h2>
          {doc.questions.some((q) => q.suggestion !== null) && (
            <p className="mt-0.5 text-xs text-muted-foreground">ZAC filled in what it would choose. Change any, or clear it to leave it open.</p>
          )}
          <div className="mt-3 space-y-3">
            {doc.questions.map((q) => (
              <div key={q.id}>
                <p className="text-sm text-foreground"><span className="mr-1 font-mono text-xs text-muted-foreground">{q.id}</span>{q.text}</p>
                {q.objects !== undefined && (
                  <p className="text-xs text-amber-300">One answer covers {q.objects.slice(0, -1).join(', ')} and {q.objects.at(-1)}: if they differ, say which is which ("{q.objects[0]}: …; {q.objects[1]}: …").</p>
                )}
                {field(q.id, q.suggestion === null ? 'Your answer' : 'Left open')}
                {q.suggestion !== null && <Suggested suggestion={q.suggestion} value={given[q.id] ?? ''} set={(text) => setGiven((prev) => ({ ...prev, [q.id]: text }))} />}
              </div>
            ))}
          </div>
        </section>
      )}
      {doc.assumptions.length > 0 && !showAssumptions && (
        <button type="button" onClick={() => setShowAssumptions(true)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ChevronRight className="h-3.5 w-3.5" /> {doc.assumptions.length === 1 ? 'One thing' : `${doc.assumptions.length} things`} left to Zoom's defaults
        </button>
      )}
      {doc.assumptions.length > 0 && showAssumptions && (
        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="flex items-center gap-1 text-sm font-medium text-foreground">
            {compact && <button type="button" onClick={() => setShowAssumptions(false)} aria-label="Fold" className="text-muted-foreground hover:text-foreground"><ChevronDown className="h-4 w-4" /></button>}
            What ZAC leaves to Zoom
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Correct any that is not what you want; the rest stand.</p>
          <div className="mt-3 space-y-3">
            {doc.assumptions.map((s) => (
              <div key={s.id}>
                <p className="text-sm text-foreground"><span className="mr-1 font-mono text-xs text-muted-foreground">{s.id}</span>{s.text}</p>
                {field(s.id, 'Only if it should be otherwise')}
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className="mr-auto text-xs text-muted-foreground">
          {doc.can_answer
            ? `Round ${doc.round} of ${doc.max_rounds}. The plan takes your answers as they are; read again when they change what the document asks for (about a minute).`
            : `Round ${doc.round} of ${doc.max_rounds}: the plan takes your answers as they are.`}
        </span>
        {doc.can_answer && (
          <button
            onClick={() => void answer(doc.id, answers)}
            disabled={busy || answers.length === 0 || onlySuggested}
            title={onlySuggested && answers.length > 0 ? 'ZAC\'s suggestions go with the plan as they are: change an answer to read again' : undefined}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-secondary disabled:opacity-50"
          >
            <CornerDownLeft className="h-4 w-4" /> Answer and read again
          </button>
        )}
        <button
          onClick={() => void plan(doc.id, answers)}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Make the plan
        </button>
      </div>
    </div>
  );
}

/** ZAC's suggestion under its question: why, and a way back to it or to leaving the question open. */
function Suggested({ suggestion, value, set }: { suggestion: { text: string; why: string }; value: string; set: (text: string) => void }) {
  const kept = value.trim() === suggestion.text;
  return (
    <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
      <span>{kept ? 'ZAC\'s suggestion' : `ZAC suggests "${suggestion.text}"`}: {suggestion.why}</span>
      {kept
        ? <button type="button" onClick={() => set('')} className="underline hover:text-foreground">leave open</button>
        : <button type="button" onClick={() => set(suggestion.text)} className="underline hover:text-foreground">use it</button>}
    </p>
  );
}
